import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Button } from "@mui/material";
import { toast } from "react-toastify";
import { DeleteModal } from "../../../../components/DeleteModal";
import { useStore } from "../../../../helpers";
import type { AnalyticsToolbarMode } from "../../../../stores/tars/tars-analytics-store";
import { safe_get } from "../../../../report-visuals";
import { useFacetExecuteEtl, useGetFacet, useSourceList } from "../../tars-hooks";
import { useNormalizeKeyFacetRefresh } from "./useNormalizeKeyFacetRefresh";
import {
    ensureNormalizeProbeDataSource,
    resolveNormalizeKeyValueOptionsKeyed,
    type NormalizeKeyDataSourceHint,
} from "./facet-normalize-key-helpers";
import { buildFacetUpdatePayload, resolveServerFacetId } from "./facet-workspace-update-helpers";
import { usePersistFacetUpdate } from "./usePersistFacetUpdate";
import FacetJsonEditorFooter from "./FacetJsonEditorFooter";
import FacetWorkspaceToolbar from "./FacetWorkspaceToolbar";
import ChartTypePasteModal from "./ChartTypePasteModal";
import FacetTableModePanel from "./FacetTableModePanel";
import FacetChartModePanel from "../../tars-charts/FacetChartModePanel";
import FacetFormPanel from "./facet-form/FacetFormPanel";
import {
    facetEtlExecuteParams,
    facetResultFromExecuteResponse,
    normalizationResultFromExecuteResponse,
} from "./facet-etl-execute-helpers";
import {
    extractFacetTableData,
    facetTableRowValueOptions,
    facetTransformColOptions,
    type FacetTableData,
} from "./facet-table-helpers";
import { applyRowSortToFacetResult, getEtlRowSort, getPivotRowSort } from "./facet-result-row-sort";
import { dataColumnHeadersForSort, labelsToAxisSort, patchMatrixAxisSort } from "./facet-table-sort-helpers";
import type { AxisSortValue } from "./facet-table-sort-helpers";
import { applyFacetTableEditsToFacetResult } from "./facet-table-persist-helpers";
import { facetTableDataToChartRows } from "./facet-chart-data-helpers";
import type { FacetResultDataSource } from "./FacetResultSourceToggle";
import { normalizeChartEntryFromParsed } from "../../tars-charts/facet-chart-entry-helpers";
import {
    applyChartImageToMore,
    ensureChartEntryId,
    isChartPushedToCloud,
    uploadChartPngToS3,
} from "../../tars-charts/facet-chart-publish-helpers";
import {
    TARS_CHART_PUBLISH_MISSING_EXPORT,
    TARS_CHART_PUBLISH_MISSING_NOTEBOOK,
    TARS_CHART_PUBLISH_SUCCESS_TOAST,
} from "../../tars-constants";
import {
    chartTypeLabel,
    normalizeChartType,
    parseFacetCharts,
    TARS_BAR_CHART,
    TARS_COLUMN_CHART,
    TARS_DONUT_CHART,
    TARS_STACKED_BAR_CHART,
} from "./facet-chart-types";
import TARSBarChart from "../../tars-charts/TARSBarChart";
import TARSColumnChart from "../../tars-charts/TARSColumnChart";
import TARSDonutChart from "../../tars-charts/TARSDonutChart";
import TARSStackedBarChart from "../../tars-charts/TARSStackedBarChart";
import {
    emptyChartConfiguration,
    type TARSBarChartTableData,
    type TARSChartConfiguration,
} from "../../tars-charts/tarsBarChartHelpers";
import type { TARSStackedBarChartTableData } from "../../tars-charts/tarsStackedBarChartHelpers";
import {
    mergeReferenceTableOptions,
    referenceTableOptionsFromSourceList,
    referenceTableOptionsFromSourceNodes,
} from "./facet-reference-table-helpers";
import { resolveEtlNodeFromDocument, resolveFacetEtlDocument } from "./facet-etl-document-helpers";
import type { SourceFile } from "../sidebar/sources-types";

interface FacetJsonWorkspacePanelProps {
    facetId: string;
}

function FacetJsonWorkspacePanelInner({ facetId }: FacetJsonWorkspacePanelProps) {
    const chartCopyRef = useRef<(() => Promise<void>) | null>(null);
    const chartExportRef = useRef<(() => Promise<Blob>) | null>(null);
    const { notebookId } = useParams<{ notebookId: string }>();
    const notebookIdNum = Number(notebookId);
    const { tarsAnalyticsStore, tarsStore } = useStore();
    const facet = tarsAnalyticsStore.facets.find((f) => f.id === facetId);
    const { analyticsToolbarMode } = tarsAnalyticsStore;
    const getFacet = useGetFacet();
    const executeEtl = useFacetExecuteEtl();
    const { persistFacetResult, persistFacetMore, isPersisting } = usePersistFacetUpdate(
        tarsAnalyticsStore,
        notebookIdNum
    );
    const [jsonEditorActive, setJsonEditorActive] = useState(false);
    const [isEditMode, setIsEditMode] = useState(true);
    const [jsonDraft, setJsonDraft] = useState("");
    const [tableSubMode, setTableSubMode] = useState<"view" | "json">("view");
    const [resultDataSource, setResultDataSource] = useState<FacetResultDataSource>("facet");
    const [tableJsonDraft, setTableJsonDraft] = useState("");
    const [chartJsonModalOpen, setChartJsonModalOpen] = useState(false);
    const [chartJsonDraft, setChartJsonDraft] = useState("");
    const [pendingChartType, setPendingChartType] = useState<string | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [optionsRefreshKey, setOptionsRefreshKey] = useState(0);
    const [normalizeKeyOptionsByKey, setNormalizeKeyOptionsByKey] = useState<Record<string, string[]>>({});
    const [loadingNormalizeKey, setLoadingNormalizeKey] = useState<string | null>(null);
    const [selectedChartIndex, setSelectedChartIndex] = useState(0);
    const prevChartsLengthRef = useRef(0);
    const taRef = useRef<HTMLTextAreaElement>(null);

    const chartsList = useMemo(
        () => parseFacetCharts(safe_get(facet, "more", undefined) as Record<string, unknown> | undefined),
        [facet?.more]
    );

    useEffect(() => {
        setSelectedChartIndex(0);
        prevChartsLengthRef.current = 0;
    }, [facetId]);

    useEffect(() => {
        const n = chartsList.length;
        if (n === 0) {
            prevChartsLengthRef.current = 0;
            return;
        }
        if (n > prevChartsLengthRef.current) {
            setSelectedChartIndex(n - 1);
        } else if (n < prevChartsLengthRef.current) {
            setSelectedChartIndex((i) => Math.min(i, n - 1));
        }
        prevChartsLengthRef.current = n;
    }, [chartsList.length]);

    useEffect(() => {
        setTableSubMode("view");
        setResultDataSource("facet");
        tarsAnalyticsStore.setChartPane("view");
        setTableJsonDraft(
            JSON.stringify((safe_get(facet, "facet_result", {}) as Record<string, unknown>) ?? {}, null, 2)
        );
    }, [facetId]);

    useEffect(() => {
        if (!facet) return;
        setJsonDraft(facet.jsonContent ?? "");
        setJsonEditorActive(Boolean(facet.jsonContent?.trim()));
    }, [facetId, facet?.jsonContent]);

    useEffect(() => {
        if (!facet) return;
        setTableJsonDraft(
            JSON.stringify((safe_get(facet, "facet_result", {}) as Record<string, unknown>) ?? {}, null, 2)
        );
    }, [facet?.facet_result, facet?.id]);

    const safeChartIndex = useMemo(
        () => (chartsList.length === 0 ? 0 : Math.min(selectedChartIndex, chartsList.length - 1)),
        [chartsList.length, selectedChartIndex]
    );

    const activeChartEntry = chartsList.length === 0 ? null : chartsList[safeChartIndex];

    useEffect(() => {
        if (!facet) return;

        const resolvedFacetId = (() => {
            if (typeof facet.facet_id === "number" && Number.isFinite(facet.facet_id)) {
                return facet.facet_id;
            }
            const parsed = Number(facet.id);
            return Number.isFinite(parsed) ? parsed : null;
        })();

        if (!resolvedFacetId) return;

        getFacet.mutate(
            { facet_id: resolvedFacetId },
            {
                onSuccess: (resp) => {
                    if (resp == null || typeof resp !== "object") return;
                    const data = resp as Record<string, unknown>;
                    /** API returns facet fields at top level (see get-facet response). */
                    const more = (safe_get(data, "more", {}) as Record<string, unknown>) ?? {};
                    const etlJson = safe_get(data, "etl_json", undefined) as
                        | Record<string, unknown>
                        | undefined;
                    const facetResult = safe_get(data, "facet_result", undefined) as
                        | Record<string, unknown>
                        | undefined;
                    const nameFromMoreRaw = safe_get(more, "name", undefined);
                    const nameFromMore = typeof nameFromMoreRaw === "string" ? nameFromMoreRaw : facet.name;
                    const jsonContent =
                        etlJson != null ? JSON.stringify(etlJson, null, 2) : (facet.jsonContent ?? "");
                    const versionRaw = safe_get(data, "version", undefined);
                    const version =
                        typeof versionRaw === "number" && Number.isFinite(versionRaw)
                            ? versionRaw
                            : undefined;

                    setJsonDraft(jsonContent);
                    setJsonEditorActive(Boolean(jsonContent.trim()));
                    tarsAnalyticsStore.updateFacet(facet.id, {
                        name: nameFromMore,
                        more,
                        etl_json: etlJson ?? {},
                        facet_result: facetResult ?? {},
                        jsonContent,
                        ...(version !== undefined ? { version } : {}),
                    });
                    setTableJsonDraft(
                        JSON.stringify((facetResult as Record<string, unknown> | undefined) ?? {}, null, 2)
                    );
                },
            }
        );
    }, [facet?.id, facet?.facet_id]);

    const tableData = useMemo(() => {
        if (!facet) {
            return { headers: [] as string[], rows: [] as Record<string, unknown>[] };
        }
        return extractFacetTableData(safe_get(facet, "facet_result", undefined));
    }, [facet?.facet_result, facet?.id]);

    const normalizationTableData = useMemo(() => {
        const nr = safe_get(facet, "facet_result.normalization_result", undefined);
        return extractFacetTableData(nr);
    }, [facet?.facet_result]);

    const chartLiveTablePayload = useMemo(() => {
        const table =
            resultDataSource === "normalized" && normalizationTableData ? normalizationTableData : tableData;
        return facetTableDataToChartRows(table);
    }, [resultDataSource, tableData, normalizationTableData]);

    const preserveChartRowOrder = useMemo(() => {
        const etlDoc = resolveFacetEtlDocument(jsonDraft, facet);
        return etlDoc != null && getEtlRowSort(etlDoc) != null;
    }, [jsonDraft, facet?.etl_json, facet]);

    const chartViewData: TARSBarChartTableData | null = useMemo(() => {
        if (!activeChartEntry) return null;
        const savedRows = safe_get(activeChartEntry, "rows", undefined);
        const savedHeaders = safe_get(activeChartEntry, "headers", undefined);
        const rows = chartLiveTablePayload?.rows ?? savedRows;
        const headers = chartLiveTablePayload?.headers ?? savedHeaders;
        if (!Array.isArray(rows) || !Array.isArray(headers)) return null;
        const titleRaw = safe_get(activeChartEntry, "title", undefined);
        const title = typeof titleRaw === "string" ? titleRaw : undefined;
        const colors = safe_get(activeChartEntry, "colors", undefined);
        const seriesColorsRaw = safe_get(activeChartEntry, "seriesColors", undefined);
        const configurationRaw = safe_get(activeChartEntry, "configuration", undefined);
        const out: TARSBarChartTableData = { rows, headers, preserveRowOrder: preserveChartRowOrder };
        if (title !== undefined) out.title = title;
        if (Array.isArray(colors)) out.colors = colors;
        if (Array.isArray(seriesColorsRaw)) {
            (out as TARSBarChartTableData & Pick<TARSStackedBarChartTableData, "seriesColors">).seriesColors =
                seriesColorsRaw;
        }
        if (configurationRaw != null && typeof configurationRaw === "object") {
            out.configuration = configurationRaw as TARSChartConfiguration;
        }
        return out;
    }, [activeChartEntry, chartLiveTablePayload, preserveChartRowOrder]);

    useEffect(() => {
        if (!activeChartEntry) {
            tarsAnalyticsStore.syncChartDraft("");
            return;
        }
        tarsAnalyticsStore.syncChartDraft(JSON.stringify(activeChartEntry, null, 2));
    }, [facet?.id, safeChartIndex, activeChartEntry, tarsAnalyticsStore]);

    const columnOptions = useMemo(
        () => facetTransformColOptions(safe_get(facet, "facet_result", undefined)),
        [facet?.facet_result, optionsRefreshKey]
    );

    const getRowValueOptions = useCallback(
        (rowKeyHint: string) =>
            facetTableRowValueOptions(safe_get(facet, "facet_result", undefined), rowKeyHint),
        [facet?.facet_result, optionsRefreshKey]
    );

    const getNormalizeKeyValueOptions = useCallback(
        (keyHint: string) => {
            const hint = keyHint.trim();
            if (!hint) return [];
            return normalizeKeyOptionsByKey[hint] ?? [];
        },
        [normalizeKeyOptionsByKey, optionsRefreshKey]
    );

    const resolvedFacetServerId = useMemo(() => {
        if (!facet) return null;
        if (typeof facet.facet_id === "number" && Number.isFinite(facet.facet_id)) {
            return facet.facet_id;
        }
        const parsed = Number(facet.id);
        return Number.isFinite(parsed) ? parsed : null;
    }, [facet?.facet_id, facet?.id]);

    const normalizeDataSourceHint = useMemo((): NormalizeKeyDataSourceHint => {
        const selectedId = tarsAnalyticsStore.selectedSourceIds[0];
        if (!selectedId) return {};
        const node = tarsStore.getNode(selectedId) as SourceFile | undefined;
        if (!node || node.type !== "dataRepo") return {};
        const esIndex = node.es_index?.trim();
        const sourceId = Number(selectedId);
        return {
            esIndex: esIndex || undefined,
            sourceId: Number.isFinite(sourceId) ? sourceId : undefined,
        };
    }, [tarsAnalyticsStore.selectedSourceIds, tarsStore]);

    const refreshNormalizeKeyFacet = useNormalizeKeyFacetRefresh({
        facetId: resolvedFacetServerId,
        notebookId: notebookIdNum,
        dataSourceHint: normalizeDataSourceHint,
        getFacet,
        executeEtl,
        onKeyFetchStart: (normalizeKey) => {
            setLoadingNormalizeKey(normalizeKey);
            setNormalizeKeyOptionsByKey((prev) => ({ ...prev, [normalizeKey]: [] }));
        },
        onKeyOptionsLoaded: (normalizeKey, options) => {
            setLoadingNormalizeKey((current) => (current === normalizeKey ? null : current));
            setNormalizeKeyOptionsByKey((prev) => ({ ...prev, [normalizeKey]: options }));
        },
        onOptionsRefresh: () => setOptionsRefreshKey((k) => k + 1),
    });

    const handleNormalizeKeyCommit = useCallback(
        (json: string, key: string) => {
            const trimmed = key.trim();
            if (!trimmed) return;

            let effectiveJson = json;
            try {
                const formParsed = JSON.parse(json) as Record<string, unknown>;
                const withDs = ensureNormalizeProbeDataSource(formParsed, normalizeDataSourceHint);
                effectiveJson = JSON.stringify(withDs, null, 2);
            } catch {
                const etlDoc = resolveFacetEtlDocument(jsonDraft, facet);
                if (etlDoc) {
                    try {
                        const formParsed = JSON.parse(json) as Record<string, unknown>;
                        const merged = {
                            ...formParsed,
                            etl: {
                                ...(resolveEtlNodeFromDocument(formParsed) ?? {}),
                                ds: safe_get(etlDoc, "etl.ds", safe_get(etlDoc, "ds", {})),
                            },
                        };
                        effectiveJson = JSON.stringify(
                            ensureNormalizeProbeDataSource(merged, normalizeDataSourceHint),
                            null,
                            2
                        );
                    } catch {
                        effectiveJson = json;
                    }
                }
            }

            setJsonDraft(effectiveJson);
            if (facet) {
                tarsAnalyticsStore.updateFacet(facet.id, { jsonContent: effectiveJson });
            }

            const fromStore = resolveNormalizeKeyValueOptionsKeyed(
                { facet_result: facet?.facet_result },
                trimmed
            );
            if (fromStore.length > 0) {
                setNormalizeKeyOptionsByKey((prev) => ({ ...prev, [trimmed]: fromStore }));
                setOptionsRefreshKey((k) => k + 1);
                return;
            }

            refreshNormalizeKeyFacet(effectiveJson, trimmed);
        },
        [facet, jsonDraft, normalizeDataSourceHint, refreshNormalizeKeyFacet, tarsAnalyticsStore]
    );

    const { data: referenceSourceListData } = useSourceList(
        Number.isFinite(notebookIdNum) && notebookIdNum > 0
            ? {
                  notebook_id: notebookIdNum,
                  parent_id: null,
                  offset: 0,
                  limit: 100,
                  fetchFullTree: true,
              }
            : null
    );

    const referenceTableOptions = mergeReferenceTableOptions(
        referenceTableOptionsFromSourceList(referenceSourceListData?.sources),
        referenceTableOptionsFromSourceNodes(tarsStore.nodes)
    );

    const syncTableDraftFromFacetResult = useCallback((saved: Record<string, unknown>) => {
        setTableJsonDraft(JSON.stringify(saved, null, 2));
        setOptionsRefreshKey((k) => k + 1);
    }, []);

    const runExecuteEtlThenPersist = useCallback(
        (
            parsed: Record<string, unknown>,
            jsonContentForStore: string,
            opts: { successToast: string; missingEtlMessage: string }
        ) => {
            if (!facet) return;

            const canPersist = buildFacetUpdatePayload(
                facet,
                {
                    etl_json: parsed,
                    facet_result: (safe_get(facet, "facet_result", {}) as Record<string, unknown>) ?? {},
                },
                notebookIdNum
            );
            if (!canPersist) {
                toast.error("Facet must exist on the server before saving");
                return;
            }

            const execParams = facetEtlExecuteParams(parsed);
            if (!execParams) {
                toast.error(opts.missingEtlMessage);
                return;
            }

            tarsAnalyticsStore.updateFacet(facet.id, {
                etl_json: parsed,
                jsonContent: jsonContentForStore,
            });

            executeEtl.mutate(
                {
                    etl_json: execParams.etl_json,
                    decimal_places: execParams.decimal_places,
                    ...(Number.isFinite(notebookIdNum) && notebookIdNum > 0
                        ? { notebook_id: notebookIdNum }
                        : {}),
                },
                {
                    onSuccess: (execResp) => {
                        if (execResp == null || typeof execResp !== "object") return;
                        const execData = execResp as Record<string, unknown>;
                        let nextFacetResult = facetResultFromExecuteResponse(execData);
                        const pivotRowSort = getPivotRowSort(parsed);
                        if (pivotRowSort) {
                            nextFacetResult = applyRowSortToFacetResult(nextFacetResult, pivotRowSort);
                        }
                        const nextNormalizationResult = normalizationResultFromExecuteResponse(execData);
                        if (nextNormalizationResult) {
                            nextFacetResult.normalization_result = nextNormalizationResult;
                        }

                        tarsAnalyticsStore.updateFacet(facet.id, {
                            facet_result: nextFacetResult,
                            etl_json: parsed,
                            jsonContent: jsonContentForStore,
                        });
                        setTableJsonDraft(JSON.stringify(nextFacetResult, null, 2));

                        void persistFacetResult({
                            facet,
                            facet_result: nextFacetResult,
                            etl_json: parsed,
                            successToast: opts.successToast,
                            missingServerMessage: "Facet must exist on the server before saving",
                            onSuccess: (saved) => {
                                syncTableDraftFromFacetResult(saved);
                                tarsAnalyticsStore.setAnalyticsToolbarMode("table");
                            },
                        });
                    },
                }
            );
        },
        [
            executeEtl,
            facet,
            notebookIdNum,
            persistFacetResult,
            syncTableDraftFromFacetResult,
            tarsAnalyticsStore,
        ]
    );

    const runExecuteEtlPreview = useCallback(
        (parsed: Record<string, unknown>, jsonContentForStore: string) => {
            if (!facet) return;

            const execParams = facetEtlExecuteParams(parsed);
            if (!execParams) {
                toast.error("JSON is missing an etl object");
                return;
            }

            executeEtl.mutate(
                {
                    etl_json: execParams.etl_json,
                    decimal_places: execParams.decimal_places,
                    ...(Number.isFinite(notebookIdNum) && notebookIdNum > 0
                        ? { notebook_id: notebookIdNum }
                        : {}),
                },
                {
                    onSuccess: (execResp) => {
                        if (execResp == null || typeof execResp !== "object") return;
                        const execData = execResp as Record<string, unknown>;
                        const nextFacetResult = facetResultFromExecuteResponse(execData);
                        const nextNormalizationResult = normalizationResultFromExecuteResponse(execData);
                        if (nextNormalizationResult) {
                            nextFacetResult.normalization_result = nextNormalizationResult;
                        }

                        setJsonDraft(jsonContentForStore);
                        tarsAnalyticsStore.updateFacet(facet.id, {
                            facet_result: nextFacetResult,
                            etl_json: parsed,
                            jsonContent: jsonContentForStore,
                        });
                        setTableJsonDraft(JSON.stringify(nextFacetResult, null, 2));
                        setOptionsRefreshKey((k) => k + 1);
                        toast.success("Transform applied — table and lists updated");
                    },
                }
            );
        },
        [executeEtl, facet, notebookIdNum, tarsAnalyticsStore]
    );

    const persistTableAxisSort = useCallback(
        (patch: { row_sort?: AxisSortValue; column_sort?: AxisSortValue }) => {
            if (!facet) return;
            const current = resolveFacetEtlDocument(jsonDraft, facet);
            if (!current) {
                toast.error("No ETL JSON to update");
                return;
            }
            const next = patchMatrixAxisSort(current, patch);
            if (!next) {
                toast.error("No pivot operation found in ETL");
                return;
            }
            const jsonContent = JSON.stringify(next, null, 2);
            setJsonDraft(jsonContent);
            runExecuteEtlThenPersist(next, jsonContent, {
                successToast: "Table order saved",
                missingEtlMessage: "ETL JSON is missing a matrix operation",
            });
        },
        [jsonDraft, facet, runExecuteEtlThenPersist]
    );

    const handleTableRowOrderChange = useCallback(
        (rowLabels: string[]) => {
            persistTableAxisSort({ row_sort: labelsToAxisSort(rowLabels) });
        },
        [persistTableAxisSort]
    );

    const handleTableColumnOrderChange = useCallback(
        (columnHeaders: string[]) => {
            persistTableAxisSort({
                column_sort: labelsToAxisSort(dataColumnHeadersForSort(columnHeaders)),
            });
        },
        [persistTableAxisSort]
    );

    if (!facet) return null;

    const hasJson = Boolean(jsonDraft.trim());
    const showZeroth = !jsonEditorActive && !hasJson;
    const hasValidJsonEtl = (() => {
        try {
            const parsed = JSON.parse(jsonDraft || "{}") as Record<string, unknown>;
            const etl = safe_get(parsed, "etl", undefined);
            return etl != null && typeof etl === "object";
        } catch {
            return false;
        }
    })();

    const typeRaw = activeChartEntry ? safe_get(activeChartEntry, "type", undefined) : undefined;
    const chartTypeForView = typeof typeRaw === "string" ? normalizeChartType(typeRaw) : TARS_COLUMN_CHART;

    const toolbarChartSummaries = chartsList.map((e) => {
        const t = safe_get(e, "type", undefined);
        const tTitle = safe_get(e, "title", undefined);
        return {
            type: typeof t === "string" ? normalizeChartType(t) : TARS_COLUMN_CHART,
            title: typeof tTitle === "string" ? tTitle : undefined,
            pushed: isChartPushedToCloud(e),
        };
    });

    const moreChartsRaw = safe_get(facet, "more.charts", undefined);
    const facetHasChartsFromMore = Array.isArray(moreChartsRaw) && moreChartsRaw.length > 0;

    const handleJsonChange = (v: string) => {
        setJsonDraft(v);
        tarsAnalyticsStore.updateFacet(facet.id, { jsonContent: v });
    };

    const handleStartJson = () => {
        setJsonEditorActive(true);
        setIsEditMode(true);
        queueMicrotask(() => taRef.current?.focus());
    };

    const handleModeChange = (mode: AnalyticsToolbarMode) => {
        if (mode !== "json" && mode !== "form" && !hasValidJsonEtl) {
            toast.error("Please enter valid JSON with etl before opening Table/Chart");
            return;
        }
        tarsAnalyticsStore.setAnalyticsToolbarMode(mode);
    };

    const handleSaveTableJson = () => {
        let parsed: Record<string, unknown>;
        try {
            parsed = JSON.parse(tableJsonDraft || "{}") as Record<string, unknown>;
        } catch {
            toast.error("Invalid table JSON format");
            return;
        }
        void persistFacetResult({
            facet,
            facet_result: parsed,
            jsonDraft,
            etlFallback: "etl-field",
            successToast: "Table JSON saved",
            missingServerMessage: "Facet must exist on the server before saving table JSON",
            onSuccess: (saved) => {
                syncTableDraftFromFacetResult(saved);
                setTableSubMode("view");
            },
        });
    };

    const handleSaveTableEdits = (editedTable: FacetTableData) => {
        const currentResult = (safe_get(facet, "facet_result", {}) as Record<string, unknown>) ?? {};
        const target =
            resultDataSource === "normalized" && normalizationTableData ? "normalization" : "result";
        const nextFacetResult = applyFacetTableEditsToFacetResult(currentResult, editedTable, target);
        return persistFacetResult({
            facet,
            facet_result: nextFacetResult,
            jsonDraft,
            successToast: "Table saved",
            missingServerMessage: "Facet must exist on the server before saving table edits",
            onSuccess: syncTableDraftFromFacetResult,
        });
    };

    const handleSaveAndExecuteEtlJson = () => {
        let parsedJson: Record<string, unknown>;
        try {
            parsedJson = JSON.parse(jsonDraft || "{}") as Record<string, unknown>;
        } catch {
            toast.error("Invalid JSON format");
            return;
        }
        const jsonContent = JSON.stringify(parsedJson, null, 2);
        runExecuteEtlThenPersist(parsedJson, jsonContent, {
            successToast: "Facet JSON saved",
            missingEtlMessage: "JSON must include etl object",
        });
    };

    const handleFormSaveAndExecute = (json: string) => {
        setJsonDraft(json);
        tarsAnalyticsStore.updateFacet(facet.id, { jsonContent: json });

        let parsed: Record<string, unknown>;
        try {
            parsed = JSON.parse(json) as Record<string, unknown>;
        } catch {
            toast.error("Invalid JSON from form");
            return;
        }

        const jsonContentNormalized = JSON.stringify(parsed, null, 2);
        runExecuteEtlThenPersist(parsed, jsonContentNormalized, {
            successToast: "Facet saved",
            missingEtlMessage: "JSON is missing an etl object",
        });
    };

    const handleFormApplyTransform = (json: string) => {
        let parsed: Record<string, unknown>;
        try {
            parsed = JSON.parse(json) as Record<string, unknown>;
        } catch {
            toast.error("Invalid JSON from form");
            return;
        }
        runExecuteEtlPreview(parsed, JSON.stringify(parsed, null, 2));
    };

    const handleResetChartJson = () => {
        if (!activeChartEntry) {
            tarsAnalyticsStore.syncChartDraft("");
            return;
        }
        tarsAnalyticsStore.syncChartDraft(JSON.stringify(activeChartEntry, null, 2));
    };

    const handleSaveActiveChartJson = () => {
        let parsed: Record<string, unknown>;
        try {
            parsed = JSON.parse(tarsAnalyticsStore.chartDraft || "{}") as Record<string, unknown>;
        } catch {
            toast.error("Invalid chart JSON format");
            return;
        }
        const normalized = normalizeChartEntryFromParsed(parsed, facet, chartTypeForView);
        if (normalized.ok === false) {
            toast.error(normalized.error);
            return;
        }
        const chartEntry = normalized.entry;
        const more = safe_get(facet, "more", undefined) as Record<string, unknown> | undefined;
        const prevMore = { ...(more ?? {}) };
        const existingChartsRaw = safe_get(prevMore, "charts", undefined);
        const existingCharts = Array.isArray(existingChartsRaw) ? [...existingChartsRaw] : [];
        const idx = safeChartIndex;
        if (idx < 0 || idx >= existingCharts.length) {
            toast.error("Chart index is out of date");
            return;
        }
        existingCharts[idx] = chartEntry;
        const nextMore = { ...prevMore, charts: existingCharts };

        persistFacetMore({
            facet,
            more: nextMore,
            successToast: "Chart JSON saved",
            missingServerMessage: "Facet must exist on the server before saving chart JSON",
            onSuccess: () => tarsAnalyticsStore.setChartPane("view"),
        });
    };

    const handleSaveChartConfig = (updatedEntry: Record<string, unknown>) => {
        const more = safe_get(facet, "more", undefined) as Record<string, unknown> | undefined;
        const prevMore = { ...(more ?? {}) };
        const existingChartsRaw = safe_get(prevMore, "charts", undefined);
        const existingCharts = Array.isArray(existingChartsRaw) ? [...existingChartsRaw] : [];
        const idx = safeChartIndex;
        if (idx < 0 || idx >= existingCharts.length) {
            toast.error("Chart index is out of date");
            return;
        }
        existingCharts[idx] = updatedEntry;
        const nextMore = { ...prevMore, charts: existingCharts };

        persistFacetMore({
            facet,
            more: nextMore,
            successToast: "Chart configuration saved",
            missingServerMessage: "Facet must exist on the server before saving config",
            onSuccess: () => tarsAnalyticsStore.setChartPane("view"),
        });
    };

    const handleDeleteChart = () => {
        const more = safe_get(facet, "more", undefined) as Record<string, unknown> | undefined;
        const prevMore = { ...(more ?? {}) };
        const existingChartsRaw = safe_get(prevMore, "charts", undefined);
        const existingCharts = Array.isArray(existingChartsRaw) ? [...existingChartsRaw] : [];
        const idx = safeChartIndex;
        if (idx < 0 || idx >= existingCharts.length) {
            toast.error("Chart is out of date");
            setConfirmOpen(false);
            return;
        }
        const nextCharts = existingCharts.filter((_, i) => i !== idx);
        const nextMore = { ...prevMore, charts: nextCharts };

        void persistFacetMore({
            facet,
            more: nextMore,
            successToast: "Chart removed",
            missingServerMessage: "Facet must exist on the server before deleting a chart",
            onSuccess: () => {
                setConfirmOpen(false);
                tarsAnalyticsStore.setChartPane("view");
            },
            onError: () => setConfirmOpen(false),
        }).catch(() => setConfirmOpen(false));
    };

    const handlePublishActiveChart = async () => {
        if (!facet) return;
        if (!Number.isFinite(notebookIdNum) || notebookIdNum <= 0) {
            toast.error(TARS_CHART_PUBLISH_MISSING_NOTEBOOK);
            return;
        }
        const serverFacetId = resolveServerFacetId(facet);
        if (serverFacetId == null || serverFacetId <= 0) {
            toast.error("Facet must exist on the server before pushing a chart");
            return;
        }
        const exportFn = chartExportRef.current;
        if (!exportFn) {
            toast.error(TARS_CHART_PUBLISH_MISSING_EXPORT);
            return;
        }
        if (!activeChartEntry) {
            toast.error("No chart selected");
            return;
        }
        try {
            // Deep clone so MobX proxies cannot drop nested `image` on stringify/upload.
            const chartEntry = JSON.parse(JSON.stringify(activeChartEntry)) as Record<
                string,
                unknown
            >;
            const chartId = ensureChartEntryId(chartEntry);
            const blob = await exportFn();
            const image = await uploadChartPngToS3({
                notebookId: notebookIdNum,
                chartId,
                blob,
            });
            const more = safe_get(facet, "more", undefined) as Record<string, unknown> | undefined;
            const nextMore = applyChartImageToMore({
                more: JSON.parse(JSON.stringify(more ?? {})) as Record<string, unknown>,
                chartIndex: safeChartIndex,
                chartEntry,
                image,
            });
            await persistFacetMore({
                facet,
                more: nextMore,
                successToast: TARS_CHART_PUBLISH_SUCCESS_TOAST,
                missingServerMessage: "Facet must exist on the server before pushing a chart",
            });
        } catch (e) {
            console.error("Push chart failed", e);
            toast.error(e instanceof Error ? e.message : "Failed to push chart");
            throw e;
        }
    };

    const handleChartTypeChosen = (chartTypeId: string) => {
        setPendingChartType(chartTypeId);
        const moreName = safe_get(facet, "more.name", undefined);
        const defaultTitle =
            (typeof moreName === "string" && moreName.trim() !== "" ? moreName : facet.name) ?? "";
        const base = (safe_get(facet, "facet_result", {}) as Record<string, unknown>) ?? {};
        const draft = {
            ...base,
            type: chartTypeId,
            title: defaultTitle,
            configuration: emptyChartConfiguration(),
        };
        setChartJsonDraft(JSON.stringify(draft, null, 2));
        setChartJsonModalOpen(true);
    };

    const handleSaveChartFromModal = () => {
        let parsed: Record<string, unknown>;
        try {
            parsed = JSON.parse(chartJsonDraft || "{}") as Record<string, unknown>;
        } catch {
            toast.error("Invalid chart JSON format");
            return;
        }
        const normalized = normalizeChartEntryFromParsed(
            parsed,
            facet,
            pendingChartType ?? TARS_COLUMN_CHART
        );
        if (normalized.ok === false) {
            toast.error(normalized.error);
            return;
        }
        const chartEntry = normalized.entry;

        const more = safe_get(facet, "more", undefined) as Record<string, unknown> | undefined;
        const prevMore = { ...(more ?? {}) };
        const existingChartsRaw = safe_get(prevMore, "charts", undefined);
        const existingCharts = Array.isArray(existingChartsRaw) ? [...existingChartsRaw] : [];
        const nextMore = {
            ...prevMore,
            charts: [...existingCharts, chartEntry],
        };

        persistFacetMore({
            facet,
            more: nextMore,
            successToast: "Chart saved",
            missingServerMessage: "Facet must exist on the server before saving chart",
            onSuccess: () => {
                setChartJsonModalOpen(false);
                setPendingChartType(null);
            },
        });
    };

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col px-4 pb-4">
            <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-slate-50/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                <FacetWorkspaceToolbar
                    facetName={facet.name}
                    mode={analyticsToolbarMode}
                    onModeChange={handleModeChange}
                    canOpenDerivedViews={hasValidJsonEtl}
                    onSelectChartType={handleChartTypeChosen}
                    chartSummaries={toolbarChartSummaries}
                    facetHasCharts={facetHasChartsFromMore}
                    selectedChartIndex={safeChartIndex}
                    onSelectChartIndex={setSelectedChartIndex}
                />
                {analyticsToolbarMode === "chart" &&
                    (chartViewData ? (
                        <FacetChartModePanel
                            onReset={handleResetChartJson}
                            onSave={handleSaveActiveChartJson}
                            onDelete={() => setConfirmOpen(true)}
                            chartCopyRef={chartCopyRef}
                            onPublishChart={handlePublishActiveChart}
                            publishDisabled={isPersisting}
                            chartPushed={isChartPushedToCloud(activeChartEntry)}
                            chartEntry={
                                activeChartEntry && chartViewData
                                    ? { ...activeChartEntry, headers: chartViewData.headers }
                                    : activeChartEntry
                            }
                            onSaveConfig={handleSaveChartConfig}
                            hasNormalizationResult={Boolean(normalizationTableData)}
                            resultDataSource={resultDataSource}
                            onResultDataSource={setResultDataSource}
                        >
                            {chartTypeForView === TARS_COLUMN_CHART ? (
                                <TARSColumnChart
                                    data={chartViewData}
                                    id={`facet-${facetId}-column`}
                                    chartCopyRef={chartCopyRef}
                                    chartExportRef={chartExportRef}
                                />
                            ) : chartTypeForView === TARS_BAR_CHART ? (
                                <TARSBarChart
                                    data={chartViewData}
                                    id={`facet-${facetId}-bar`}
                                    chartCopyRef={chartCopyRef}
                                    chartExportRef={chartExportRef}
                                />
                            ) : chartTypeForView === TARS_DONUT_CHART ? (
                                <TARSDonutChart
                                    data={chartViewData}
                                    id={`facet-${facetId}-donut`}
                                    chartCopyRef={chartCopyRef}
                                    chartExportRef={chartExportRef}
                                />
                            ) : chartTypeForView === TARS_STACKED_BAR_CHART ? (
                                <TARSStackedBarChart
                                    data={chartViewData as TARSStackedBarChartTableData}
                                    id={`facet-${facetId}-stacked`}
                                    chartCopyRef={chartCopyRef}
                                    chartExportRef={chartExportRef}
                                />
                            ) : (
                                <TARSColumnChart
                                    data={chartViewData}
                                    id={`facet-${facetId}-column-fallback`}
                                    chartCopyRef={chartCopyRef}
                                    chartExportRef={chartExportRef}
                                />
                            )}
                        </FacetChartModePanel>
                    ) : (
                        <div className="flex flex-1 items-center justify-center px-8 py-16 text-[13px] font-medium text-slate-400">
                            Use Chart + to choose a chart type and save your data.
                        </div>
                    ))}
                {analyticsToolbarMode === "table" && (
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                        <FacetTableModePanel
                            tableData={tableData}
                            normalizationTableData={normalizationTableData}
                            resultDataSource={resultDataSource}
                            onResultDataSource={setResultDataSource}
                            tableSubMode={tableSubMode}
                            onTableSubMode={setTableSubMode}
                            tableJsonDraft={tableJsonDraft}
                            onTableJsonDraft={setTableJsonDraft}
                            onResetTableJson={() =>
                                setTableJsonDraft(
                                    JSON.stringify(
                                        (safe_get(facet, "facet_result", {}) as Record<string, unknown>) ??
                                            {},
                                        null,
                                        2
                                    )
                                )
                            }
                            onSaveTableJson={handleSaveTableJson}
                            onRowOrderChange={
                                resultDataSource === "facet" ? handleTableRowOrderChange : undefined
                            }
                            onColumnOrderChange={
                                resultDataSource === "facet" ? handleTableColumnOrderChange : undefined
                            }
                            sortOrderPersistDisabled={executeEtl.isLoading || isPersisting}
                            onSaveTableEdits={handleSaveTableEdits}
                            isSavingTableEdits={isPersisting}
                        />
                    </div>
                )}
                {analyticsToolbarMode === "form" && (
                    <FacetFormPanel
                        jsonContent={jsonDraft}
                        getRowValueOptions={getRowValueOptions}
                        getNormalizeKeyValueOptions={getNormalizeKeyValueOptions}
                        columnOptions={columnOptions}
                        referenceTableOptions={referenceTableOptions}
                        dataBankSources={referenceSourceListData?.sources}
                        tarsNodes={tarsStore.nodes}
                        onSaveAndExecute={handleFormSaveAndExecute}
                        onApplyTransform={handleFormApplyTransform}
                        onNormalizeKeyCommit={handleNormalizeKeyCommit}
                        projectQuestionKeys={tarsAnalyticsStore.projectQuestionKeys}
                        isApplyingTransform={executeEtl.isLoading}
                        isLoadingNormalizeKeyValues={loadingNormalizeKey !== null || executeEtl.isLoading}
                        optionsRefreshKey={optionsRefreshKey}
                    />
                )}
                {analyticsToolbarMode === "json" && (
                    <div className="relative flex min-h-0 flex-1 flex-col">
                        {showZeroth ? (
                            <Button
                                type="button"
                                onClick={handleStartJson}
                                className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 transition hover:bg-[#eceff2]/70"
                            >
                                <div
                                    className="flex h-[64px] w-[64px] items-center justify-center rounded-[14px] bg-[#1f86d6] text-[24px] font-bold leading-none text-white shadow-[0_10px_18px_rgba(31,134,214,0.35)]"
                                    aria-hidden
                                >
                                    <span className="font-mono tracking-tight">{`{/}`}</span>
                                </div>
                                <span className="max-w-[180px] text-center text-[14px] font-medium leading-5 text-[#4b5563]">
                                    Click here to write your JSON
                                </span>
                            </Button>
                        ) : (
                            <>
                                {isEditMode ? (
                                    <textarea
                                        ref={taRef}
                                        value={jsonDraft}
                                        onChange={(e) => handleJsonChange(e.target.value)}
                                        onBlur={() => {
                                            if (!jsonDraft.trim()) setJsonEditorActive(false);
                                        }}
                                        placeholder="{}"
                                        spellCheck={false}
                                        className="min-h-0 flex-1 resize-none bg-slate-50/80 p-4 font-mono text-[12px] leading-relaxed text-slate-800 outline-none placeholder:text-slate-400"
                                    />
                                ) : (
                                    <pre className="min-h-0 flex-1 overflow-auto bg-slate-50/80 p-4 font-mono text-[12px] leading-relaxed text-slate-800">
                                        {jsonDraft || "{}"}
                                    </pre>
                                )}
                                <FacetJsonEditorFooter
                                    isEditMode={isEditMode}
                                    onToggleMode={() => setIsEditMode((prev) => !prev)}
                                    onSaveAndExecuteJson={handleSaveAndExecuteEtlJson}
                                />
                            </>
                        )}
                    </div>
                )}
            </div>
            <ChartTypePasteModal
                open={chartJsonModalOpen}
                value={chartJsonDraft}
                onChange={setChartJsonDraft}
                onClose={() => {
                    setChartJsonModalOpen(false);
                    setPendingChartType(null);
                }}
                onCreate={handleSaveChartFromModal}
                chartTypeHint={pendingChartType ? `Chart type: ${chartTypeLabel(pendingChartType)}` : null}
            />
            <DeleteModal
                isOpen={confirmOpen}
                id="facet-chart-delete"
                title="Delete chart?"
                desc={`This will remove “${chartViewData?.title?.trim() ?? "this chart"}” from this facet. This cannot be undone.`}
                onCancelClick={() => setConfirmOpen(false)}
                onDeleteClick={() => handleDeleteChart()}
            />
        </div>
    );
}

export default observer(FacetJsonWorkspacePanelInner);
